// IndexedDB home of the upload queue: database `mesh-capture`, store `uploads`, one row per
// photo keyed by its id. Every write tells this tab's listeners and, over the
// `mesh-capture-queue` BroadcastChannel, every other tab, so they all show the same queue.

import type { QueuedUpload } from "@/lib/upload-queue";

const DB_NAME = "mesh-capture";
const DB_VERSION = 1;
const STORE = "uploads";
const CHANNEL = "mesh-capture-queue";

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  const opening = new Promise<IDBDatabase>((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("This browser cannot keep photos for upload"));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) {
        request.result.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    request.onsuccess = () => {
      const db = request.result;
      // Another tab upgrading the schema: step aside so it can, and reopen next call.
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    request.onerror = () => reject(request.error ?? new Error("Could not open the upload queue"));
    request.onblocked = () => reject(new Error("Close the app's other tabs and try again"));
  });
  // A failed open is retried on the next call rather than cached.
  opening.catch(() => {
    dbPromise = null;
  });
  dbPromise = opening;
  return opening;
}

/** Run one request in its own transaction; resolves once the transaction commits. */
async function run<T>(
  mode: IDBTransactionMode,
  op: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = op(tx.objectStore(STORE));
    tx.oncomplete = () => resolve(request.result);
    tx.onerror = () => reject(tx.error ?? new Error("The upload queue could not be saved"));
    tx.onabort = () => reject(tx.error ?? new Error("The upload queue could not be saved"));
  });
}

const listeners = new Set<() => void>();
let channel: BroadcastChannel | null | undefined;

function getChannel(): BroadcastChannel | null {
  if (channel !== undefined) return channel;
  channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL);
  if (channel) {
    channel.onmessage = () => {
      for (const listener of listeners) listener();
    };
  }
  return channel;
}

function notify() {
  for (const listener of listeners) listener();
  getChannel()?.postMessage("changed");
}

export async function putItem(item: QueuedUpload): Promise<void> {
  await run("readwrite", (store) => store.put(item));
  notify();
}

export async function getItem(id: string): Promise<QueuedUpload | undefined> {
  // Rows are only ever written by putItem, so they have the QueuedUpload shape.
  const row: QueuedUpload | undefined = await run("readonly", (store) => store.get(id));
  return row;
}

export async function listItems(): Promise<QueuedUpload[]> {
  const rows: QueuedUpload[] = await run("readonly", (store) => store.getAll());
  return rows;
}

export async function deleteItem(id: string): Promise<void> {
  await run("readwrite", (store) => store.delete(id));
  notify();
}

/** Called after any change to the queue, in this tab or another. Returns the unsubscribe. */
export function subscribe(listener: () => void): () => void {
  getChannel();
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
